// Places a list of places one after another on a schedule, day by day, inside
// their opening hours (Schedule.addEventOpeningHoursAware) — the core of Build
// My Trip's "build the schedule from these places" (BuildMyTripWizard.js
// buildScheduleFromAddedPlaces), shared so the Featured-guide fix tools test
// exactly the app's own scheduling (2026-10-30).
//
// Look-ahead: when the next place in route order doesn't fit today (closed
// today, or the visit would run past closing), the next place in the order
// that DOES fit today goes first, and the closed one waits for a day it is
// open. Before this, a place closed on the trip's first day moved the whole
// cursor to the next day and day 1 stayed empty (a Monday start with a museum
// first in the route), and a mid-day closure ended that day early.
//
// No long waits (2026-11-01): a place that fits today only after a wait (a church closed for lunch until
// 15:30) doesn't make the day wait for it when another place in the order can be visited sooner: the first
// place in route order that starts within MAX_WAIT of the soonest possible start goes next. Before this,
// Cefalù's day stood empty from 12:30 to 15:30 while La Rocca, the beach and the harbour were left out.
//
// Best time of day (2026-11-01): a "sunset" or "evening" place (Place.BestTime) waits until nothing else fits
// the day, so it ends the day instead of landing at 11:00 because it was next on the route.
//
// opts: { scheduleName, dayDate (DateTime cursor), startH, endH, roundingTo,
//   placesModel, maxAllowedDate, paceMultiplier, hardEndTime,
//   beforeEach(cursor) -> cursor  (e.g. insert lunch),
//   latestEndH  optional: a stop must end by this hour or it goes to the next
//               morning (the app itself lets an open-late place run to 23:00) }
// Returns { dayDate, firstEventOfDay, lastPlace, thisDay, placed: [], skipped: [] }.
const MAX_WAIT = 30; // minutes

export default function schedulePlacesInOrder(schedule, ordered, opts) {
  const { scheduleName, startH, endH, roundingTo, placesModel, maxAllowedDate, paceMultiplier, hardEndTime, beforeEach, latestEndH } = opts;
  const queue = (ordered || []).filter(Boolean);
  const placed = [];
  const skipped = [];
  let cursor = opts.dayDate;
  let firstEventOfDay = opts.firstEventOfDay ?? true;
  let lastPlace = opts.lastPlace ?? null;
  let thisDay = opts.thisDay ?? cursor;
  const endsInTime = (end) => latestEndH == null || end.hour + end.minute / 60 <= latestEndH;
  // the visit's start if the place fits today, else null
  const startToday = (p) => {
    const end = schedule.addEventOpeningHoursAware(p, scheduleName, cursor, lastPlace, roundingTo, firstEventOfDay, startH, endH, placesModel, maxAllowedDate, paceMultiplier, hardEndTime, { dryRun: true, probe: false });
    return end !== null && end.hasSame(cursor, "day") && endsInTime(end) ? end.minus({ minutes: (p.Duration || 60) * (paceMultiplier || 1) }) : null;
  };
  // the first place in order that fits today without waiting much longer than the soonest one would
  const late = (p) => p.BestTime === "sunset" || p.BestTime === "evening";
  const pickToday = () => {
    const daytime = queue.map((p) => (late(p) ? null : startToday(p)));
    const starts = daytime.some(Boolean) ? daytime : queue.map(startToday);
    const soonest = starts.reduce((m, t) => (t && (!m || t < m) ? t : m), null);
    if (!soonest) return -1;
    return starts.findIndex((t) => t && t.diff(soonest, "minutes").minutes <= MAX_WAIT);
  };
  const nextMorning = () => cursor.startOf("day").plus({ days: 1 }).set({ hour: startH, minute: 0, second: 0, millisecond: 0 });
  while (queue.length) {
    if (maxAllowedDate && cursor.startOf("day") >= maxAllowedDate) {
      skipped.push(...queue.splice(0));
      break;
    }
    if (beforeEach) cursor = beforeEach(cursor);
    let i = pickToday();
    if (i === -1 && latestEndH != null && !firstEventOfDay) {
      // nothing fits in what's left of today: start tomorrow morning
      cursor = nextMorning();
      thisDay = cursor;
      firstEventOfDay = true;
      lastPlace = null;
      continue;
    }
    if (i === -1) i = 0; // nothing fits today: the next place finds its own open day
    const place = queue.splice(i, 1)[0];
    const before = schedule.events.length;
    const end = schedule.addEventOpeningHoursAware(place, scheduleName, cursor, lastPlace, roundingTo, firstEventOfDay, startH, endH, placesModel, maxAllowedDate, paceMultiplier, hardEndTime);
    if (end === null) {
      skipped.push(place);
      continue;
    }
    if (!endsInTime(end)) {
      // placed on a later day but too late in it: take it back out
      const added = schedule.events.find((e) => String(e.Places) === String(place.ID) && e.End === end.toISO());
      if (added) schedule.deleteEvent(added);
      else schedule.events.splice(before);
      skipped.push(place);
      continue;
    }
    cursor = end;
    // as Build My Trip always did: the first stop that lands on a new day keeps firstEventOfDay set
    if (cursor.day !== thisDay.day) {
      thisDay = cursor;
      firstEventOfDay = true;
    } else firstEventOfDay = false;
    lastPlace = place;
    placed.push(place);
  }
  return { dayDate: cursor, firstEventOfDay, lastPlace, thisDay, placed, skipped };
}
